import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft, AlertCircle, CheckCircle, Activity, Timer, Footprints, Trophy, Play } from 'lucide-react';
import SiteHeaderV2 from '@/components/website/SiteHeaderV2';
import SiteFooterV2 from '@/components/website/SiteFooterV2';
import PageTitle from '@/components/website/PageTitle';

/* eslint-disable react/no-unescaped-entities */

const ButeykoMethodPage = () => {
  const navigate = useNavigate();
  
  const principles = [
    {
      title: "Le CO2 n'est pas un déchet",
      description: "Contrairement à la croyance populaire, le dioxyde de carbone n'est pas simplement un déchet à éliminer. C'est un régulateur vital de la respiration et de l'oxygénation.",
      icon: AlertCircle
    },
    {
      title: "L'hyperventilation chronique",
      description: "Respirer plus que nécessaire (même légèrement) évacue trop de CO2, créant un déséquilibre qui perturbe l'ensemble du métabolisme.",
      icon: Activity
    },
    {
      title: "L'effet Bohr",
      description: "Un taux de CO2 optimal (40 mmHg) est nécessaire pour que l'oxygène se détache de l'hémoglobine et atteigne les tissus. Moins de CO2 = moins d'oxygénation paradoxalement.",
      icon: CheckCircle
    },
    {
      title: "Augmenter la tolérance au CO2",
      description: "L'objectif est de réhabituer le corps à un niveau normal de CO2, améliorant ainsi l'oxygénation, réduisant l'essoufflement et optimisant la santé.",
      icon: Trophy
    }
  ];

  const benefits = [
    "Réduction de l'asthme et des problèmes respiratoires",
    "Amélioration des performances sportives",
    "Diminution de l'anxiété et du stress",
    "Meilleur sommeil (réduction des apnées)",
    "Augmentation de l'énergie et réduction de la fatigue",
    "Amélioration de la concentration",
    "Réduction de la congestion nasale",
    "Optimisation de l'oxygénation cellulaire"
  ];

  const boltInterpretation = [
    { range: "< 10 secondes", status: "Très faible", color: "bg-slate-50 border-slate-200 text-sky-700", description: "Hyperventilation sévère, nombreux symptômes probables" },
    { range: "10-20 secondes", status: "Faible", color: "bg-sky-50 border-sky-200 text-slate-700", description: "Hyperventilation modérée, symptômes fréquents" },
    { range: "20-30 secondes", status: "Moyen", color: "bg-sky-50 border-yellow-200 text-slate-700", description: "Tolérance acceptable mais perfectible" },
    { range: "30-40 secondes", status: "Bon", color: "bg-sky-50 border-sky-200 text-sky-600", description: "Bonne tolérance au CO2, peu de symptômes" },
    { range: "> 40 secondes", status: "Excellent", color: "bg-sky-50 border-sky-200 text-sky-700", description: "Tolérance optimale, santé respiratoire excellente" }
  ];

  const breathlessnessInterpretation = [
    { range: "< 30 pas", status: "Très faible", color: "bg-slate-50 border-slate-200 text-sky-700", description: "Tolérance très limitée" },
    { range: "30-50 pas", status: "Faible", color: "bg-sky-50 border-sky-200 text-slate-700", description: "Tolérance en dessous de la normale" },
    { range: "50-70 pas", status: "Moyen", color: "bg-sky-50 border-yellow-200 text-slate-700", description: "En progression vers l'optimal" },
    { range: "70-100 pas", status: "Bon", color: "bg-sky-50 border-sky-200 text-sky-600", description: "Bonne tolérance fonctionnelle" },
    { range: "> 100 pas", status: "Excellent", color: "bg-sky-50 border-sky-200 text-sky-700", description: "Tolérance optimale, niveau athlétique" }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FFFFFF", fontFamily: "Figtree, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500&family=Figtree:wght@400;500;600&display=swap');
      `}</style>
      
      <SiteHeaderV2 />
      
      {/* Hero Section */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: '#F8FAFC' }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <PageTitle
                kicker="Technique"
                title="Méthode"
                highlight="Buteyko"
                subtitle="Découvrez les techniques révolutionnaires pour optimiser votre respiration en augmentant votre tolérance au CO2 et améliorer votre santé globale."
                align="left"
                className="mb-8"
              />
              <Link
                to="/breathing/oxygen-advantage"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-medium transition-all hover:shadow-lg hover:-translate-y-1"
                style={{ backgroundColor: '#0EA5E9', color: '#FFFFFF' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                </svg>
                Le protocole complet
              </Link>
            </div>
            <div className="flex justify-center">
              <img 
                src="/lungs-pink.jpg"
                alt="Poumons - Illustration artistique"
                className="w-72 h-72 lg:w-96 lg:h-96 object-cover rounded-full shadow-2xl"
                style={{ border: '4px solid #E0F2FE', objectPosition: 'center 55%' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Origins */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 sm:px-12">
          <h2 
            className="text-3xl sm:text-4xl mb-8"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: '#0F172A' }}
          >
            Les <span style={{ color: '#0EA5E9', fontWeight: 400 }}>Origines</span>
          </h2>
          
          <div className="space-y-6" style={{ color: '#475569' }}>
            <p className="text-lg leading-relaxed">
              La <strong style={{ color: '#0F172A' }}>méthode Buteyko</strong> a été développée dans les années 1950 par le Dr. Konstantin Buteyko, 
              médecin et physiologiste russe. Après des décennies de recherches, il découvrit que de nombreuses 
              maladies chroniques (asthme, hypertension, anxiété) étaient liées à l'hyperventilation chronique.
            </p>
            
            <p className="text-lg leading-relaxed">
              Plus récemment, <strong style={{ color: '#0F172A' }}>Patrick McKeown</strong> a popularisé et modernisé ces principes à travers 
              son programme <strong style={{ color: '#0F172A' }}>Oxygen Advantage</strong>, intégrant les dernières recherches scientifiques 
              sur la physiologie respiratoire et l'optimisation des performances.
            </p>

            <div className="bg-sky-50 border-l-4 border-sky-500 p-6 rounded-lg my-6">
              <h3 className="font-medium mb-2"> Le Paradoxe de la Respiration</h3>
              <p className="text-slate-700">
                <strong>Respirer plus n'améliore pas l'oxygénation, cela la réduit.</strong> L'hyperventilation 
                évacue trop de CO2, ce qui empêche l'oxygène de se libérer dans les tissus (effet Bohr). 
                C'est pourquoi quelqu'un en hyperventilation peut se sentir essoufflé malgré un niveau d'oxygène 
                sanguin normal.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Principles */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-medium mb-8 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Les 4 Principes Fondamentaux
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {principles.map((principle, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center flex-shrink-0">
                      <principle.icon className="w-6 h-6 text-sky-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium mb-2">{principle.title}</h3>
                      <p className="text-slate-600">{principle.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-medium mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Bénéfices Prouvés de la Méthode
          </h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-sky-50 rounded-xl">
                <CheckCircle className="w-5 h-5 text-sky-600 flex-shrink-0" />
                <span className="text-slate-700">{benefit}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 bg-sky-50 border-l-4 border-sky-500 rounded-lg">
            <h3 className="font-bold text-slate-800 mb-2">🔬 Validations Scientifiques</h3>
            <p className="text-slate-700">
              Plus de 80 études cliniques ont validé l'efficacité de la méthode Buteyko, notamment pour 
              l'asthme (réduction de 70% des symptômes), l'anxiété, les troubles du sommeil et l'amélioration 
              des performances sportives. L'Oxygen Advantage est utilisé par des athlètes olympiques et des 
              équipes professionnelles.
            </p>
          </div>
        </div>
      </section>

      {/* Tests Section */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-medium mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Évaluez Votre Tolérance au CO2
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Deux tests simples pour mesurer votre tolérance au CO2 et identifier votre niveau d'hyperventilation. 
              Ces tests sont disponibles dans les défis de l'application.
            </p>
          </div>

          {/* BOLT Test */}
          <Card className="border-0 shadow-xl mb-8">
            <div className="h-3 bg-gradient-to-r from-sky-500 to-sky-600" />
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center flex-shrink-0">
                  <Timer className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <h3 className="text-2xl font-medium">BOLT Score (Body Oxygen Level Test)</h3>
                    <button
                      onClick={() => navigate('/evaluer-respiration?test=bolt')}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-sky-500 to-sky-600 text-white text-sm font-semibold hover:from-violet-600 hover:to-purple-700 transition-all shadow-md hover:shadow-lg"
                    >
                      <Play size={16} />
                      Faites le test
                    </button>
                  </div>
                  <p className="text-slate-600">
                    Le test de référence pour mesurer votre tolérance au CO2 au repos.
                  </p>
                </div>
              </div>

              <div className="rounded-xl p-6 mb-6">
                <h4 className="font-medium mb-4">📋 Procédure du test :</h4>
                <ol className="space-y-3 text-slate-700">
                  <li className="flex gap-3">
                    <span className="font-bold text-sky-600 flex-shrink-0">1.</span>
                    <span>Asseyez-vous confortablement et respirez normalement pendant 2-3 minutes.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-sky-600 flex-shrink-0">2.</span>
                    <span>Prenez une inspiration normale (pas profonde) puis expirez normalement.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-sky-600 flex-shrink-0">3.</span>
                    <span>Pincez votre nez et démarrez le chronomètre.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-sky-600 flex-shrink-0">4.</span>
                    <span>Retenez votre souffle jusqu'au <strong>premier besoin de respirer</strong> (pas jusqu'au maximum !). Vous devez ressentir :</span>
                  </li>
                  <li className="ml-8 space-y-1">
                    <p>• Une légère contraction du diaphragme</p>
                    <p>• Un besoin de respirer, mais pas de panique</p>
                    <p>• La sensation doit être <strong>confortable</strong></p>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-sky-600 flex-shrink-0">5.</span>
                    <span>Arrêtez le chronomètre et notez le temps. Reprenez une respiration normale (pas une grande inspiration).</span>
                  </li>
                </ol>
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-center gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-sky-600">25+</div>
                    <div className="text-sm text-slate-500">secondes</div>
                  </div>
                  <div className="text-2xl text-slate-400">=</div>
                  <div className="text-lg font-semibold text-slate-700">Valeur Normale Minimale</div>
                </div>
                <p className="text-center text-slate-600 text-sm">
                  Un BOLT score de 25 secondes ou plus indique une tolérance au CO2 acceptable. 
                  L'objectif optimal est d'atteindre 40 secondes ou plus.
                </p>
              </div>

              <div className="space-y-3">
                <h4 className="font-medium mb-3">Interprétation des résultats :</h4>
                {boltInterpretation.map((item, index) => (
                  <div key={index} className={`p-4 rounded-lg border-2 ${item.color}`}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-bold">{item.range}</span>
                      <span className="text-sm font-semibold">{item.status}</span>
                    </div>
                    <p className="text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Maximum Breathlessness Test */}
          <Card className="border-0 shadow-xl">
            <div className="h-3 bg-gradient-to-r from-sky-500 to-sky-600" />
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center flex-shrink-0">
                  <Footprints className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-medium mb-2">Maximum Breathlessness Test (Test de Marche)</h3>
                  <p className="text-slate-600">
                    Évalue votre tolérance au CO2 en mouvement et pendant l'effort.
                  </p>
                </div>
              </div>

              <div className="rounded-xl p-6 mb-6">
                <h4 className="font-medium mb-4">📋 Procédure du test :</h4>
                <ol className="space-y-3 text-slate-700">
                  <li className="flex gap-3">
                    <span className="font-bold text-sky-600 flex-shrink-0">1.</span>
                    <span>Debout, respirez normalement pendant 1-2 minutes.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-sky-600 flex-shrink-0">2.</span>
                    <span>Prenez une inspiration normale puis expirez normalement.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-sky-600 flex-shrink-0">3.</span>
                    <span>Pincez votre nez et commencez à marcher à un rythme normal.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-sky-600 flex-shrink-0">4.</span>
                    <span>Continuez à retenir votre souffle tout en marchant jusqu'à ressentir un <strong>fort besoin de respirer</strong>.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-sky-600 flex-shrink-0">5.</span>
                    <span>Comptez vos pas. Arrêtez lorsque vous sentez que vous DEVEZ respirer (inconfort modéré à important).</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-sky-600 flex-shrink-0">6.</span>
                    <span>Reprenez une respiration normale (pas de grande inspiration). Notez le nombre de pas.</span>
                  </li>
                </ol>
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-center gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-sky-600">60-70</div>
                    <div className="text-sm text-slate-500">pas</div>
                  </div>
                  <div className="text-2xl text-slate-400">=</div>
                  <div className="text-lg font-semibold text-slate-700">Valeur Normale</div>
                </div>
                <p className="text-center text-slate-600 text-sm">
                  60-70 pas ou plus indique une bonne tolérance au CO2 pendant l'effort. 
                  Les athlètes de haut niveau peuvent atteindre 100+ pas.
                </p>
              </div>

              <div className="space-y-3">
                <h4 className="font-medium mb-3">Interprétation des résultats :</h4>
                {breathlessnessInterpretation.map((item, index) => (
                  <div key={index} className={`p-4 rounded-lg border-2 ${item.color}`}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-bold">{item.range}</span>
                      <span className="text-sm font-semibold">{item.status}</span>
                    </div>
                    <p className="text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Important Notes */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-6 h-6 text-slate-600" />
            </div>
            <div>
              <h3 className="font-medium text-xl mb-3"> Points Importants</h3>
              <ul className="space-y-2 text-slate-800">
                <li>• Ces tests ne sont <strong>pas</strong> des compétitions. Ne poussez pas au maximum absolu.</li>
                <li>• Le BOLT mesure le premier besoin de respirer (confort), pas la capacité maximale d'apnée.</li>
                <li>• Effectuez ces tests au repos, pas après un effort ou un repas.</li>
                <li>• Testez-vous le matin pour des résultats plus précis.</li>
                <li>• Si vous avez des problèmes cardiaques ou respiratoires graves, consultez un médecin avant.</li>
                <li>• Ces tests sont disponibles dans la section <strong>"Défis"</strong> de l'application pour un suivi régulier.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How to Improve */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-medium mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Comment Améliorer Vos Scores ?
          </h2>
          
          <div className="space-y-6">
            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sky-600 font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-2">Pratiquez les Petites Pauses (Buteyko)</h3>
                    <p className="text-slate-600">
                      L'exercice de base : inspiration légère (2s), expiration douce (3s), puis pause de 5-10s. 
                      Répétez 6 fois, 3 fois par jour. C'est l'exercice le plus efficace pour augmenter la tolérance au CO2.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sky-600 font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-2">Respirez par le Nez en Permanence</h3>
                    <p className="text-slate-600">
                      Même pendant l'effort. Utilisez du tape médical la nuit si vous respirez par la bouche en dormant. 
                      La respiration nasale augmente naturellement le CO2.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sky-600 font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-2">Ralentissez Votre Respiration</h3>
                    <p className="text-slate-600">
                      Visez 6-10 respirations par minute au repos. Utilisez les exercices de cohérence cardiaque 
                      (5s inspiration / 5s expiration) pour vous entraîner.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sky-600 font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-2">Entraînement en Hypoventilation</h3>
                    <p className="text-slate-600">
                      Pendant la marche ou l'exercice léger, réduisez légèrement votre ventilation (respirer moins 
                      que ce que votre corps "demande"). Commencez doucement et progressivement.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-8 p-6 bg-sky-50 border-l-4 border-sky-500 rounded-lg">
            <h4 className="font-medium mb-2">📈 Progression Attendue</h4>
            <p className="text-slate-700">
              Avec une pratique régulière (15-20 minutes par jour), vous pouvez vous attendre à une amélioration 
              de 1-2 secondes par semaine sur votre BOLT score. Un score de 40 secondes est atteignable en 3-6 mois 
              pour la plupart des personnes motivées.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-medium mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Testez Votre Tolérance au CO2 Maintenant
          </h2>
          <p className="text-slate-600 text-lg mb-8 max-w-2xl mx-auto">
            Accédez aux défis BOLT Score et Maximum Breathlessness Test dans l'application. 
            Suivez vos progrès et améliorez votre santé respiratoire semaine après semaine.
          </p>
          <Link to="/app-landing">
            <button className="px-10 py-4 text-lg rounded-full bg-cyan-500 text-white hover:bg-cyan-600 shadow-lg font-semibold transition-colors">
              Accéder aux tests et exercices
            </button>
          </Link>
        </div>
      </section>

      <SiteFooterV2 />
    </div>
  );
};

export default ButeykoMethodPage;
